import { useEffect, useState } from "react";
import { Bell, MessageCircle } from "lucide-react";
import { api, patch, post } from "../../api";

interface DiscordConfig {
  configured: boolean;
  enabled: boolean;
  notifyStatus: boolean;
  notifyCrash: boolean;
  notifyPlayerJoin: boolean;
  notifyPlayerLeave: boolean;
  notifyAdminActions: boolean;
  notifyBackups: boolean;
  notifyUpdates: boolean;
  presenceSummaryInterval: number;
  includePlatformId: boolean;
  includeJoinCode: boolean;
}

const emptyConfig: DiscordConfig = {
  configured: false,
  enabled: false,
  notifyStatus: true,
  notifyCrash: true,
  notifyPlayerJoin: true,
  notifyPlayerLeave: true,
  notifyAdminActions: false,
  notifyBackups: false,
  notifyUpdates: true,
  presenceSummaryInterval: 0,
  includePlatformId: false,
  includeJoinCode: false,
};

const eventOptions: Array<{ key: keyof DiscordConfig; label: string; hint: string }> = [
  { key: "notifyStatus", label: "Servidor online e offline", hint: "Mudanças repetidas são agrupadas." },
  { key: "notifyCrash", label: "Falhas e atenção necessária", hint: "Crash, mods e pouco espaço." },
  { key: "notifyPlayerJoin", label: "Entrada de jogador", hint: "Somente transições confirmadas pelo servidor." },
  { key: "notifyPlayerLeave", label: "Saída de jogador", hint: "Inclui o tempo exato da sessão confirmada." },
  { key: "notifyAdminActions", label: "Ações administrativas", hint: "Desativado por padrão para evitar ruído." },
  { key: "notifyBackups", label: "Backups", hint: "Criação e falhas de pontos de restauração." },
  { key: "notifyUpdates", label: "Atualizações", hint: "Novas versões e resultado de atualização." },
];

export function DiscordSettings() {
  const [config, setConfig] = useState<DiscordConfig>(emptyConfig);
  const [webhook, setWebhook] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    void api<DiscordConfig>("/system/discord").then((next) => setConfig({ ...emptyConfig, ...next })).catch((reason: unknown) => {
      setError(reason instanceof Error ? reason.message : "Não foi possível carregar o Discord.");
    });
  }, []);

  const save = async (clearWebhook = false) => {
    setError("");
    setMessage("");
    try {
      const result = await patch<DiscordConfig>("/system/discord", {
        newWebhookUrl: webhook,
        enabled: clearWebhook ? false : config.enabled,
        notifyStatus: config.notifyStatus,
        notifyCrash: config.notifyCrash,
        notifyPlayerJoin: config.notifyPlayerJoin,
        notifyPlayerLeave: config.notifyPlayerLeave,
        notifyAdminActions: config.notifyAdminActions,
        notifyBackups: config.notifyBackups,
        notifyUpdates: config.notifyUpdates,
        presenceSummaryInterval: config.presenceSummaryInterval,
        includePlatformId: config.includePlatformId,
        includeJoinCode: config.includeJoinCode,
        clearWebhook,
      });
      setConfig(result);
      setWebhook("");
      setMessage(clearWebhook ? "Webhook removido" : "Alertas salvos");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Não foi possível salvar o Discord.");
    }
  };

  const test = async () => {
    setError("");
    try {
      await post("/system/discord/test", {});
      setMessage("Mensagem de teste enviada");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Não foi possível enviar o teste.");
    }
  };

  const toggle = (key: keyof DiscordConfig, checked: boolean) => setConfig({ ...config, [key]: checked });

  return <section className="panel discord-settings">
    <div className="panel-head"><div><span className="eyebrow">ALERTAS</span><h2>Discord</h2></div><Bell size={20}/></div>
    <p>Receba eventos confiáveis em embeds. O webhook fica criptografado e nunca retorna ao navegador.</p>
    <div className="discord-layout"><div className="discord-controls">
    <label className="field"><span>{config.configured ? "Novo webhook (deixe vazio para manter)" : "URL do webhook"}</span><input type="password" value={webhook} onChange={(event) => setWebhook(event.target.value)} placeholder="https://discord.com/api/webhooks/…" autoComplete="off"/></label>
    <label className="check-row"><input type="checkbox" checked={config.enabled} onChange={(event) => toggle("enabled", event.target.checked)}/><span><b>Ativar integração</b><small>Entregas usam fila persistente e não bloqueiam o servidor.</small></span></label>
    <div className="settings-options">
      {eventOptions.map((option) => <label className="check-row" key={option.key}><input type="checkbox" checked={Boolean(config[option.key])} onChange={(event) => toggle(option.key, event.target.checked)}/><span><b>{option.label}</b><small>{option.hint}</small></span></label>)}
    </div>
    <label className="field"><span>Card de presença</span><select value={config.presenceSummaryInterval} onChange={(event) => setConfig({ ...config, presenceSummaryInterval: Number(event.target.value) })}><option value={0}>Desativado</option><option value={1}>A cada minuto</option><option value={5}>A cada 5 minutos</option><option value={15}>A cada 15 minutos</option><option value={30}>A cada 30 minutos</option><option value={60}>A cada hora</option></select><small>Atualiza uma única mensagem por servidor; entradas e saídas antecipam a atualização.</small></label>
    <label className="check-row"><input type="checkbox" checked={config.includePlatformId} onChange={(event) => toggle("includePlatformId", event.target.checked)}/><span><b>Incluir ID da plataforma</b><small>Exibe o identificador técnico do jogador nos eventos.</small></span></label>
    <label className="check-row"><input type="checkbox" checked={config.includeJoinCode} onChange={(event) => toggle("includeJoinCode", event.target.checked)}/><span><b>Incluir código para entrar</b><small>Desativado por padrão para não divulgar acesso no Discord.</small></span></label>
    <div className="inline-actions"><button className="primary small" onClick={() => void save()}>Salvar alertas</button><button className="secondary small" disabled={!config.configured} onClick={() => void test()}>Enviar teste</button>{config.configured && <button className="text-button" onClick={() => void save(true)}>Remover webhook</button>}</div>
    {message && <div className="notice success">{message}</div>}
    {error && <div className="notice error">{error}</div>}
    </div><aside className="discord-preview" aria-label="Prévia da mensagem no Discord"><div className="discord-preview-head"><MessageCircle size={16}/><b>Prévia no Discord</b></div><div className="discord-embed"><i/><div><span className="discord-bot">Reborn Valheim Control <small>APP</small></span><h3>🟢 Lucas entrou</h3><p>Acceptance Hall</p><small>Online há alguns segundos</small></div></div><p>A aparência final pode variar conforme o dispositivo.</p></aside></div>
  </section>;
}
